import { useEffect, useRef, useState } from "react";
import { ChevronDown, Lock, MapPin, Layers } from "lucide-react";
import { useSite } from "@/context/SiteContext";

/** Top-bar site selector. Renders nothing for super_admin or while loading. */
export default function SiteSelector() {
  const { loaded, visibleSites, activeSiteId, setActiveSite, isAllSites, isSingleSite, canPickAllSites, activeSite } = useSite();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!loaded || visibleSites.length === 0) return null;

  // Locked single-site UX
  if (isSingleSite) {
    return (
      <div className="vb-pill" data-testid="site-selector-locked" title="You are scoped to this site">
        <Lock size={12} /> {visibleSites[0]?.name || "Site"}
      </div>
    );
  }

  const label = isAllSites ? "All sites" : (activeSite?.name || "Select site");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="vb-btn ghost"
        onClick={() => setOpen((o) => !o)}
        data-testid="site-selector"
        aria-haspopup="listbox" aria-expanded={open}
      >
        {isAllSites ? <Layers size={14} /> : <MapPin size={14} />}
        {label}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="absolute left-0 mt-2 vb-card p-1 z-20"
          style={{
            minWidth: 240,
            maxWidth: "calc(100vw - 24px)",
          }}
          role="listbox"
          data-testid="site-selector-menu"
        >
          {canPickAllSites && (
            <button
              type="button" role="option" aria-selected={isAllSites}
              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${isAllSites ? "vb-tab active" : "hover:bg-black/5"}`}
              onClick={() => { setActiveSite("all"); setOpen(false); }}
              data-testid="site-option-all"
            >
              <Layers size={14} /> All sites
            </button>
          )}
          {visibleSites.map((s) => (
            <button
              key={s.id} type="button" role="option" aria-selected={s.id === activeSiteId}
              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${s.id === activeSiteId ? "vb-tab active" : "hover:bg-black/5"}`}
              onClick={() => { setActiveSite(s.id); setOpen(false); }}
              data-testid={`site-option-${s.id}`}
            >
              <MapPin size={14} /> {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
